import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { REVISAO_RAPIDA } from '../data/revisaoRapida';

export const HORA_INICIO_PADRAO = '07:00';
export const HORA_FIM_PADRAO = '22:00';
const INTERVALO_MINUTOS = 90;

// Matérias que mais caem em prova policial — recebem peso maior na rotação,
// mas continuam dividindo espaço com o resto do bloco do usuário.
const MATERIAS_PRIORITARIAS = [
  'direito_constitucional',
  'direito_penal',
  'direito_administrativo',
  'direito_processual_penal',
  'lingua_portuguesa'
];
const PESO_PRIORITARIA = 3;
const PESO_NORMAL = 1;

const TAMANHO_MAX_TITULO = 60;
const TAMANHO_MAX_CORPO = 120;

// iOS mata o service worker em segundo plano poucos segundos depois do app sair de
// foreground, então os setTimeout agendados lá dentro (ver service-worker.js) nunca
// disparam com o app fechado. Pra esses dispositivos usamos um workaround: dispara 1
// notificação na hora que o app abre + agenda o resto do dia com setTimeout na própria
// página (só entrega enquanto o app fica aberto em foreground/background recente).
export function ehIOS() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const iosClassico = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const iPadOsComoMac = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  return iosClassico || iPadOsComoMac;
}

// No iOS, a API de notificações só funciona quando o site foi instalado na Tela de
// Início (modo standalone) — dentro do Safari normal ela nem chega a pedir permissão.
export function estaInstaladoComoApp() {
  if (typeof window === 'undefined') return false;
  return Boolean(window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone);
}

let timersPaginaIOS = [];

function truncar(texto, tamanho) {
  const limpo = String(texto).replace(/\s+/g, ' ').trim();
  return limpo.length > tamanho ? `${limpo.slice(0, tamanho - 1)}…` : limpo;
}

const frasesPorMateria = () => REVISAO_RAPIDA.reduce((acc, item, idx) => {
  if (!item.materia || !item.titulo || !item.frase) return acc;
  (acc[item.materia] = acc[item.materia] || []).push({ ...item, id: `${item.materia}-${idx}` });
  return acc;
}, {});

// Gera os horários do dia a cada 1h30 dentro da janela [horaInicio, horaFim].
export function gerarHorarios(horaInicio, horaFim) {
  const [hi, mi] = (horaInicio || HORA_INICIO_PADRAO).split(':').map(Number);
  const [hf, mf] = (horaFim || HORA_FIM_PADRAO).split(':').map(Number);
  const inicioMin = hi * 60 + mi;
  const fimMin = Math.max(hf * 60 + mf, inicioMin);
  const horarios = [];
  for (let t = inicioMin; t <= fimMin; t += INTERVALO_MINUTOS) {
    const h = String(Math.floor(t / 60)).padStart(2, '0');
    const m = String(t % 60).padStart(2, '0');
    horarios.push(`${h}:${m}`);
  }
  return horarios;
}

function horarioParaDataHoje(horario) {
  const [h, m] = horario.split(':').map(Number);
  const data = new Date();
  data.setHours(h, m, 0, 0);
  return data;
}

// Dedupe diário (frases já usadas hoje + última matéria enviada) guardado no
// dispositivo — a chave já inclui a data, então reseta sozinha todo dia.
function chaveEstadoHoje() {
  const hoje = new Date().toISOString().split('T')[0];
  return `notificacoesLocais_${hoje}`;
}

function carregarEstadoHoje() {
  try {
    const bruto = localStorage.getItem(chaveEstadoHoje());
    if (!bruto) return { cardsUsados: [], ultimaMateria: null };
    const dados = JSON.parse(bruto);
    return { cardsUsados: dados.cardsUsados || [], ultimaMateria: dados.ultimaMateria || null };
  } catch {
    return { cardsUsados: [], ultimaMateria: null };
  }
}

function salvarEstadoHoje(estado) {
  try {
    localStorage.setItem(chaveEstadoHoje(), JSON.stringify(estado));
  } catch {
    // localStorage indisponível (modo privado etc.) — segue sem persistir dedupe
  }
}

// Sorteia a matéria dentre as disponíveis, com peso maior pras mais cobradas em prova.
function escolherMateria(materiasDisponiveis, ultimaMateria) {
  const semRepetir = materiasDisponiveis.length > 1
    ? materiasDisponiveis.filter((m) => m !== ultimaMateria)
    : materiasDisponiveis;
  const pool = semRepetir.length > 0 ? semRepetir : materiasDisponiveis;
  const pesos = pool.map((m) => (MATERIAS_PRIORITARIAS.includes(m) ? PESO_PRIORITARIA : PESO_NORMAL));
  const total = pesos.reduce((a, b) => a + b, 0);
  let sorteio = Math.random() * total;
  for (let i = 0; i < pool.length; i++) {
    sorteio -= pesos[i];
    if (sorteio <= 0) return pool[i];
  }
  return pool[pool.length - 1];
}

// Escolhe uma frase de revisão da matéria, ainda não usada hoje.
function escolherFrase(materiaId, cardsUsados) {
  const candidatos = (frasesPorMateria()[materiaId] || []).filter((item) => !cardsUsados.includes(item.id));
  if (candidatos.length === 0) return null;
  return candidatos[Math.floor(Math.random() * candidatos.length)];
}

// Sorteia matéria + frase pro próximo envio, dado o dedupe acumulado até aqui.
// Usado tanto pro disparo imediato quanto pelos dois agendadores (SW e página).
function proximaNotificacao(materiasDoUsuario, cardsUsados, ultimaMateria) {
  const materiasDisponiveis = (materiasDoUsuario || []).filter(
    (m) => frasesPorMateria()[m]?.some((item) => !cardsUsados.includes(item.id))
  );
  if (materiasDisponiveis.length === 0) return null;
  const materiaId = escolherMateria(materiasDisponiveis, ultimaMateria);
  const item = escolherFrase(materiaId, cardsUsados);
  if (!item) return null;
  return { materiaId, item };
}

// Título = só o nome do conceito (a matéria já aparece no ícone/nome do app na
// notificação do sistema, repetir vira ruído). Corpo = a frase que precisa ser
// decorada de fato — é o conteúdo que importa, então ela é o que fica em destaque.
function montarNotificacao(materiaId, item, tag) {
  return {
    titulo: truncar(item.titulo, TAMANHO_MAX_TITULO),
    corpo: truncar(item.frase, TAMANHO_MAX_CORPO),
    url: `/estudo/${materiaId}`,
    tag
  };
}

export const registrarServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) return null;
  return navigator.serviceWorker.register('/service-worker.js');
};

// Dispara uma notificação com um conceito novo imediatamente (usado quando o app abre
// no iOS, já que não dá pra confiar em nada agendado em segundo plano) e consome o
// dedupe do dia, pra ela não ser repetida nos horários agendados em seguida.
export const dispararNotificacaoImediata = async (materiasDoUsuario) => {
  if (!('Notification' in window) || Notification.permission !== 'granted') return null;
  const registration = await navigator.serviceWorker.ready.catch(() => null);
  if (!registration) return null;

  const { cardsUsados, ultimaMateria } = carregarEstadoHoje();
  const proxima = proximaNotificacao(materiasDoUsuario, cardsUsados, ultimaMateria);
  if (!proxima) return null;

  const notificacao = montarNotificacao(proxima.materiaId, proxima.item, `imediata-${Date.now()}`);
  await registration.showNotification(notificacao.titulo, {
    body: notificacao.corpo,
    icon: '/icon-192.png',
    badge: '/icon-192.png',
    tag: notificacao.tag,
    data: { url: notificacao.url }
  });

  salvarEstadoHoje({ cardsUsados: [...cardsUsados, proxima.item.id], ultimaMateria: proxima.materiaId });
  return proxima.materiaId;
};

// Workaround pro iOS: em vez de mandar os horários pro service worker (que é
// suspenso poucos segundos depois do app sair de foreground e nunca chega a disparar
// os setTimeout de lá), agenda cada notificação com setTimeout na própria página.
// Só entrega enquanto o app continuar aberto/ativo — é best-effort por natureza.
function agendarNotificacoesDoDiaPagina(materiasDoUsuario, opcoes = {}) {
  timersPaginaIOS.forEach((id) => clearTimeout(id));
  timersPaginaIOS = [];

  const { horaInicio, horaFim } = opcoes;
  const horarios = gerarHorarios(horaInicio, horaFim);
  let { cardsUsados, ultimaMateria } = carregarEstadoHoje();
  const agora = Date.now();

  let agendadas = 0;
  for (const horario of horarios) {
    const delay = horarioParaDataHoje(horario).getTime() - agora;
    if (delay <= 0) continue;

    const proxima = proximaNotificacao(materiasDoUsuario, cardsUsados, ultimaMateria);
    if (!proxima) break; // esgotou as frases do dia

    const notificacao = montarNotificacao(proxima.materiaId, proxima.item, `revisao-${horario}`);
    const id = setTimeout(() => {
      navigator.serviceWorker.ready.then((registration) => registration.showNotification(notificacao.titulo, {
        body: notificacao.corpo,
        icon: '/icon-192.png',
        badge: '/icon-192.png',
        tag: notificacao.tag,
        data: { url: notificacao.url }
      })).catch(() => {});
    }, delay);
    timersPaginaIOS.push(id);

    cardsUsados = [...cardsUsados, proxima.item.id];
    ultimaMateria = proxima.materiaId;
    agendadas++;
  }

  salvarEstadoHoje({ cardsUsados, ultimaMateria });
  return agendadas;
}

// Agenda os horários da janela [horaInicio, horaFim] que ainda não passaram hoje.
// Deve ser chamado toda vez que o app abre (o usuário precisa abrir 1x/dia pra
// reagendar o restante, já que o agendamento — SW ou página — pode ser encerrado
// quando ocioso). No iOS usa o workaround de disparo imediato + setTimeout na página.
export const agendarNotificacoesDoDia = async (materiasDoUsuario, opcoes = {}) => {
  if (!('Notification' in window) || Notification.permission !== 'granted') return 'sem_permissao';
  if (!('serviceWorker' in navigator)) return 'nao_suportado';

  await registrarServiceWorker();
  const registration = await navigator.serviceWorker.ready.catch(() => null);
  if (!registration || !registration.active) return 'sw_nao_pronto';

  if (ehIOS()) {
    await dispararNotificacaoImediata(materiasDoUsuario);
    const agendadas = agendarNotificacoesDoDiaPagina(materiasDoUsuario, opcoes);
    return agendadas > 0 ? 'agendado' : 'ativado_sem_horarios_hoje';
  }

  const { horaInicio, horaFim } = opcoes;
  const horarios = gerarHorarios(horaInicio, horaFim);
  let { cardsUsados, ultimaMateria } = carregarEstadoHoje();

  const agora = Date.now();
  const notificacoes = [];
  for (const horario of horarios) {
    const delay = horarioParaDataHoje(horario).getTime() - agora;
    if (delay <= 0) continue;

    const proxima = proximaNotificacao(materiasDoUsuario, cardsUsados, ultimaMateria);
    if (!proxima) break; // esgotou as frases do dia

    notificacoes.push({ delay, ...montarNotificacao(proxima.materiaId, proxima.item, `revisao-${horario}`) });
    cardsUsados = [...cardsUsados, proxima.item.id];
    ultimaMateria = proxima.materiaId;
  }

  salvarEstadoHoje({ cardsUsados, ultimaMateria });

  registration.active.postMessage({ type: 'AGENDAR_NOTIFICACOES', notificacoes });
  return notificacoes.length > 0 ? 'agendado' : 'ativado_sem_horarios_hoje';
};

export const cancelarNotificacoesAgendadas = async () => {
  timersPaginaIOS.forEach((id) => clearTimeout(id));
  timersPaginaIOS = [];
  const registration = await navigator.serviceWorker.ready.catch(() => null);
  registration?.active?.postMessage({ type: 'CANCELAR_NOTIFICACOES' });
};

// 'agendado' | 'ativado_sem_horarios_hoje' | 'permissao_negada' | 'nao_suportado'
// | 'sw_nao_pronto' | 'ios_instalar_app' | 'erro' | 'ios_erro'
export const ativarNotificacoes = async (uid, materiasDoUsuario, opcoes = {}) => {
  if (!('Notification' in window)) return 'nao_suportado';
  // No iOS, a API de notificação só existe de fato quando o site foi instalado na
  // Tela de Início — dentro do Safari normal o pedido de permissão falha silenciosamente
  // (ou lança erro), o que antes aparecia pro usuário como o genérico "não foi possível
  // ativar". Detectando isso antes, mostramos a instrução certa em vez do erro genérico.
  if (ehIOS() && !estaInstaladoComoApp()) return 'ios_instalar_app';
  try {
    const permissao = await Notification.requestPermission();
    if (permissao !== 'granted') return 'permissao_negada';

    await updateDoc(doc(db, 'usuarios', uid), { notificacoesAtivadas: true });
    const resultado = await agendarNotificacoesDoDia(materiasDoUsuario, opcoes);
    return resultado === 'sem_permissao' ? 'permissao_negada' : resultado;
  } catch (erro) {
    console.error('Erro ao ativar notificações:', erro);
    return ehIOS() ? 'ios_erro' : 'erro';
  }
};

export const desativarNotificacoes = async (uid) => {
  try {
    await cancelarNotificacoesAgendadas();
    await updateDoc(doc(db, 'usuarios', uid), { notificacoesAtivadas: false });
    return 'desativado';
  } catch (erro) {
    console.error('Erro ao desativar notificações:', erro);
    return 'erro';
  }
};

// Persiste a janela de horário escolhida pelo usuário (não mexe no agendamento em si —
// quem chama decide se reagenda em seguida, ex: só se as notificações já estiverem ativas).
export const salvarJanelaNotificacao = async (uid, horaInicio, horaFim) => {
  await updateDoc(doc(db, 'usuarios', uid), {
    notificacaoHoraInicio: horaInicio,
    notificacaoHoraFim: horaFim
  });
};

// Mostra uma notificação de teste imediatamente, sem passar pelo agendamento do dia
// nem consumir o dedupe diário (não marca a frase como "usada hoje").
export const testarNotificacaoAgora = async (materiasDoUsuario) => {
  if (!('Notification' in window) || Notification.permission !== 'granted') return 'sem_permissao';
  const registration = await navigator.serviceWorker.ready.catch(() => null);
  if (!registration) return 'sw_nao_pronto';

  const materiasDisponiveis = (materiasDoUsuario || []).filter((m) => frasesPorMateria()[m]?.length);
  if (materiasDisponiveis.length === 0) return 'sem_flashcards';

  const materiaId = escolherMateria(materiasDisponiveis, null);
  const item = escolherFrase(materiaId, []);
  if (!item) return 'sem_flashcards';

  const notificacao = montarNotificacao(materiaId, item, 'teste');
  await registration.showNotification(notificacao.titulo, {
    body: notificacao.corpo,
    icon: '/icon-192.png',
    badge: '/icon-192.png',
    tag: notificacao.tag,
    data: { url: notificacao.url }
  });
  return 'enviado';
};
